import { useState } from "react";

import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";

import { useAddLicence } from "../../hooks/useAddLicence";
import styles from "./AddLicenceModal.module.css";

const MAX_QUANTITY = 100;

/**
 * Self-serve licence top-up. Re-enters the current user's password (verified server-side) and
 * adds the chosen number of licenced users (one by default) on confirm. A wrong password or an
 * out-of-range quantity shows inline, not as a toast, so the modal stays open to retry.
 */
export const AddLicenceModal = ({ isOpen, onClose, onSuccess, tenantVal }) => {
  const [password, setPassword] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [error, setError] = useState("");
  const addLicence = useAddLicence(tenantVal);

  const handleClose = () => {
    setPassword("");
    setQuantity(1);
    setError("");
    onClose();
  };

  const handleQuantityChange = (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setQuantity("");
      return;
    }
    const parsed = parseInt(raw, 10);
    if (!Number.isNaN(parsed)) {
      setQuantity(parsed);
    }
  };

  const handleConfirm = async () => {
    if (!password) {
      setError("Please enter your password.");
      return;
    }
    if (!quantity || quantity < 1 || quantity > MAX_QUANTITY) {
      setError(`Enter a quantity between 1 and ${MAX_QUANTITY}.`);
      return;
    }

    try {
      await addLicence.mutateAsync({ password, quantity });
      const added = quantity;
      setPassword("");
      setQuantity(1);
      setError("");
      onSuccess(added);
    } catch (err) {
      setError(err?.response?.data?.message || "Incorrect password.");
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} title="Add a licence" width={480}>
      <p className={styles.message}>
        You've used all your licenced users. Choose how many to add and
        re-enter your password to confirm — they'll be added to your invoice
        next month.
      </p>
      <label className={styles.fieldLabel} htmlFor="add-licence-quantity">
        Number of licences
      </label>
      <input
        id="add-licence-quantity"
        type="number"
        min={1}
        max={MAX_QUANTITY}
        className={`form-control ${styles.quantityInput}`}
        value={quantity}
        onChange={handleQuantityChange}
        autoFocus
        onBlur={() => {
          if (!quantity || quantity < 1) setQuantity(1);
          else if (quantity > MAX_QUANTITY) setQuantity(MAX_QUANTITY);
        }}
      />
      <label className={styles.fieldLabel} htmlFor="add-licence-password">
        Your password
      </label>
      <input
        id="add-licence-password"
        type="password"
        className={`form-control ${styles.passwordInput}`}
        placeholder="Your password"
        value={password}
        onChange={(e) => {
          setPassword(e.target.value);
          setError("");
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleConfirm();
        }}
      />
      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.actions}>
        <Button
          variant="outline"
          size="medium"
          onClick={handleClose}
          disabled={addLicence.isPending}
        >
          Cancel
        </Button>
        <Button
          size="medium"
          onClick={handleConfirm}
          disabled={addLicence.isPending}
        >
          {addLicence.isPending ? "Adding..." : "Add licence"}
        </Button>
      </div>
    </Dialog>
  );
};
